import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FormattedMessage, IntlShape, defineMessages, useIntl} from 'react-intl'

import './AudioDeviceCheck.css'

/** Separador de la lista de comprobaciones. Simbolo, no texto. */
const DOT_SEP = ' · '
/** Espacio entre el icono y su etiqueta. */
const SPACE = ' '

/**
 * Iconos del asistente. Son simbolos, no texto: se escriben igual en los dos
 * idiomas y todos van con `aria-hidden`, asi que el lector de pantalla lee la
 * etiqueta de al lado.
 */
const GLYPH = {
  headset: '🎧',
  mic: '🎙️',
  micPermission: '🎤',
  speaker: '🔊',
  check: '✅',
  bulb: '💡',
  lock: '🔒',
  play: '▶',
  replay: '↺',
  record: '⏺',
  ok: '✓',
  ko: '✕',
  warn: '!',
} as const

interface AudioDeviceCheckProps {
  show: boolean
  onClose: () => void
}

type StepIndex = 0 | 1 | 2

const STORAGE_INPUT = 'policytools_audio_input'
const STORAGE_OUTPUT = 'policytools_audio_output'
const METER_SEGMENTS = 16

// Firefox is the recommended browser for PolicyDial: on some Windows machines
// the Intel Smart Sound (SST) driver mutes all audio when Chrome/Edge grab the
// mic in "communications" mode; Firefox uses a different audio role and avoids it.
const isRecommendedBrowser = () => {
  const ua = navigator.userAgent
  return /Firefox/.test(ua) && !/Seamonkey/i.test(ua)
}

const browserLabel = (intl: IntlShape) => {
  const ua = navigator.userAgent
  if (/Firefox/.test(ua)) return intl.formatMessage(M.browserFirefox)
  if (/Edg/.test(ua)) return intl.formatMessage(M.browserEdge)
  if (/Chrome|Chromium/.test(ua)) return intl.formatMessage(M.browserChrome)
  if (/Safari/.test(ua)) return intl.formatMessage(M.browserSafari)
  return intl.formatMessage(M.browserUnknown)
}

const supportsSinkId = () =>
  typeof (HTMLMediaElement.prototype as unknown as {setSinkId?: unknown}).setSinkId === 'function'

// Step-by-step instructions to unblock the mic — the browser won't let us do it
// programmatically, so we point the agent to the exact control for their browser.
const M = defineMessages({
  // ── Cabecera ──
  dialogLabel: {id: 'telephony.audioCheck.dialogLabel', defaultMessage: 'Audio and device check'},
  title: {id: 'telephony.audioCheck.title', defaultMessage: 'Audio & Device Check'},
  subtitle: {
    id: 'telephony.audioCheck.subtitle',
    defaultMessage: 'A quick test so the first thing a customer hears is you, not silence.',
  },
  close: {id: 'common.action.close', defaultMessage: 'Close'},

  // ── Pasos ──
  stepMic: {id: 'telephony.audioCheck.step.mic', defaultMessage: 'Microphone'},
  stepSpeaker: {id: 'telephony.audioCheck.step.speaker', defaultMessage: 'Speaker'},
  stepReady: {id: 'telephony.audioCheck.step.ready', defaultMessage: 'Ready'},

  // ── Micrófono ──
  micTitle: {id: 'telephony.audioCheck.mic.title', defaultMessage: 'Test your microphone'},
  micHint: {
    id: 'telephony.audioCheck.mic.hint',
    defaultMessage: 'This is what the customer will hear from you.',
  },
  micDefault: {id: 'telephony.audioCheck.mic.default', defaultMessage: 'Default microphone'},
  micFallbackName: {
    id: 'telephony.audioCheck.mic.fallbackName',
    defaultMessage: 'Microphone {id}',
  },
  micPrompt: {
    id: 'telephony.audioCheck.mic.prompt',
    defaultMessage: 'Talk normally — the bar should move as you speak.',
  },
  micLevelLabel: {
    id: 'telephony.audioCheck.mic.levelLabel',
    defaultMessage: 'Live microphone level',
  },
  recording: {id: 'telephony.audioCheck.recording', defaultMessage: 'Recording…'},
  playingBack: {id: 'telephony.audioCheck.playingBack', defaultMessage: 'Playing back…'},
  recordAgain: {id: 'telephony.audioCheck.recordAgain', defaultMessage: 'Record again'},
  recordAndPlay: {
    id: 'telephony.audioCheck.recordAndPlay',
    defaultMessage: 'Record & play back',
  },
  heardYourself: {
    id: 'telephony.audioCheck.heardYourself',
    defaultMessage: 'Heard yourself clearly?',
  },
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  grantAccess: {
    id: 'telephony.audioCheck.grantAccess',
    defaultMessage: 'Grant microphone access',
  },
  micBlocked: {
    id: 'telephony.audioCheck.micBlocked',
    defaultMessage: 'Microphone is blocked for this site',
  },
  tryAgain: {id: 'telephony.audioCheck.tryAgain', defaultMessage: 'Try again'},
  micOk: {id: 'telephony.audioCheck.micOk', defaultMessage: 'We can hear your microphone'},
  micBad: {
    id: 'telephony.audioCheck.micBad',
    defaultMessage: 'Try another microphone from the list above',
  },

  // ── Altavoz ──
  speakerTitle: {id: 'telephony.audioCheck.speaker.title', defaultMessage: 'Test your speaker'},
  speakerHint: {
    id: 'telephony.audioCheck.speaker.hint',
    defaultMessage: 'This is what you will hear from the customer.',
  },
  speakerDefault: {
    id: 'telephony.audioCheck.speaker.default',
    defaultMessage: 'Default speaker',
  },
  speakerFallbackName: {
    id: 'telephony.audioCheck.speaker.fallbackName',
    defaultMessage: 'Speaker {id}',
  },
  speakerSystemNote: {
    id: 'telephony.audioCheck.speaker.systemNote',
    defaultMessage: 'This browser plays the test on the system default output.',
  },
  playTestSound: {id: 'telephony.audioCheck.playTestSound', defaultMessage: 'Play test sound'},
  didYouHear: {id: 'telephony.audioCheck.didYouHear', defaultMessage: 'Did you hear it?'},
  speakerOk: {id: 'telephony.audioCheck.speakerOk', defaultMessage: 'Speaker is working'},
  speakerBad: {
    id: 'telephony.audioCheck.speakerBad',
    defaultMessage: 'Try another speaker from the list above',
  },

  // ── Resumen ──
  readyTitle: {id: 'telephony.audioCheck.ready.title', defaultMessage: "You're set"},
  readyHint: {
    id: 'telephony.audioCheck.ready.hint',
    defaultMessage: 'Last checks before you take calls.',
  },
  checkMicAccess: {
    id: 'telephony.audioCheck.check.micAccess',
    defaultMessage: 'Microphone access',
  },
  granted: {id: 'telephony.audioCheck.check.granted', defaultMessage: 'Granted'},
  blockedFromBar: {
    id: 'telephony.audioCheck.check.blocked',
    defaultMessage: 'Blocked — allow it from the address bar',
  },
  notGrantedYet: {
    id: 'telephony.audioCheck.check.notGranted',
    defaultMessage: 'Not granted yet',
  },
  grant: {id: 'telephony.audioCheck.check.grant', defaultMessage: 'Grant'},
  checkBrowser: {id: 'telephony.audioCheck.check.browser', defaultMessage: 'Browser'},
  checkConnection: {id: 'telephony.audioCheck.check.connection', defaultMessage: 'Connection'},
  secureHttps: {id: 'telephony.audioCheck.check.secure', defaultMessage: 'Secure (HTTPS)'},
  notSecure: {id: 'telephony.audioCheck.check.notSecure', defaultMessage: 'Not secure'},
  summaryOk: {
    id: 'telephony.audioCheck.summary.ok',
    defaultMessage: 'Your headset is working',
  },
  summarySpeakerPending: {
    id: 'telephony.audioCheck.summary.speakerPending',
    defaultMessage: 'Speaker still untested',
  },
  summaryMicPending: {
    id: 'telephony.audioCheck.summary.micPending',
    defaultMessage: 'Microphone still untested',
  },
  summaryNonePending: {
    id: 'telephony.audioCheck.summary.nonePending',
    defaultMessage: 'Mic and speaker not tested yet',
  },
  summaryBothPassed: {
    id: 'telephony.audioCheck.summary.bothPassed',
    defaultMessage: 'Mic and speaker both passed.',
  },
  summaryOptional: {
    id: 'telephony.audioCheck.summary.optional',
    defaultMessage: 'You can still continue — testing is optional.',
  },
  tip: {
    id: 'telephony.audioCheck.tip',
    defaultMessage:
      '<b>PolicyDial uses your default device.</b> Set the one you tested as the default in your system sound settings so every call uses it.',
  },

  // ── Pie ──
  skipForNow: {id: 'telephony.audioCheck.skipForNow', defaultMessage: 'Skip for now'},
  back: {id: 'common.action.back', defaultMessage: 'Back'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  continueToPolicyDial: {
    id: 'telephony.audioCheck.continue',
    defaultMessage: 'Continue to PolicyDial',
  },

  // ── Navegador recomendado ──
  browserFirefox: {
    id: 'telephony.audioCheck.browser.firefox',
    defaultMessage: 'Firefox — recommended',
  },
  browserEdge: {
    id: 'telephony.audioCheck.browser.edge',
    defaultMessage: 'Microsoft Edge — Firefox works best',
  },
  browserChrome: {
    id: 'telephony.audioCheck.browser.chrome',
    defaultMessage: 'Chrome — Firefox works best',
  },
  browserSafari: {
    id: 'telephony.audioCheck.browser.safari',
    defaultMessage: 'Safari — Firefox works best',
  },
  browserUnknown: {
    id: 'telephony.audioCheck.browser.unknown',
    defaultMessage: 'Unknown browser — Firefox works best',
  },

  // ── Cómo desbloquear el micrófono, por navegador ──
  unblockFfIcon: {
    id: 'telephony.audioCheck.unblock.ffIcon',
    defaultMessage: 'Click the 🎤 / 🛡 icon on the left of the address bar',
  },
  unblockFfRemove: {
    id: 'telephony.audioCheck.unblock.ffRemove',
    defaultMessage: 'Remove the “Blocked” state for the microphone',
  },
  unblockFfReload: {
    id: 'telephony.audioCheck.unblock.ffReload',
    defaultMessage: 'Reload the page, then press Try again',
  },
  unblockLock: {
    id: 'telephony.audioCheck.unblock.lock',
    defaultMessage: 'Click the 🔒 lock icon to the left of the address bar',
  },
  unblockEdgePerms: {
    id: 'telephony.audioCheck.unblock.edgePerms',
    defaultMessage: 'Open “Permissions for this site” → set Microphone to Allow',
  },
  unblockSiteSettings: {
    id: 'telephony.audioCheck.unblock.siteSettings',
    defaultMessage: 'Open “Site settings” → set Microphone to Allow',
  },
  unblockPressTryAgain: {
    id: 'telephony.audioCheck.unblock.pressTryAgain',
    defaultMessage: 'Press Try again',
  },
})

/** Devuelve los pasos ya traducidos: se pintan en una lista dentro del aviso. */
const permissionUnblockSteps = (intl: IntlShape): string[] => {
  const ua = navigator.userAgent
  if (/Firefox/.test(ua)) {
    return [M.unblockFfIcon, M.unblockFfRemove, M.unblockFfReload].map((m) =>
      intl.formatMessage(m)
    )
  }
  if (/Edg/.test(ua)) {
    return [M.unblockLock, M.unblockEdgePerms, M.unblockPressTryAgain].map((m) =>
      intl.formatMessage(m)
    )
  }
  return [M.unblockLock, M.unblockSiteSettings, M.unblockPressTryAgain].map((m) =>
    intl.formatMessage(m)
  )
}

const AudioDeviceCheck = ({show, onClose}: AudioDeviceCheckProps) => {
  const intl = useIntl()
  const [step, setStep] = useState<StepIndex>(0)

  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([])
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([])
  const [selectedInput, setSelectedInput] = useState<string>(
    () => localStorage.getItem(STORAGE_INPUT) || ''
  )
  const [selectedOutput, setSelectedOutput] = useState<string>(
    () => localStorage.getItem(STORAGE_OUTPUT) || ''
  )

  const [micPermission, setMicPermission] = useState<'granted' | 'prompt' | 'denied' | 'unknown'>(
    'unknown'
  )
  const [micLevelDetected, setMicLevelDetected] = useState(false)
  const [micConfirmed, setMicConfirmed] = useState<boolean | null>(null)
  const [speakerConfirmed, setSpeakerConfirmed] = useState<boolean | null>(null)

  const [recording, setRecording] = useState(false)
  const [playingBack, setPlayingBack] = useState(false)
  const [recordingDone, setRecordingDone] = useState(false)
  const [tonePlaying, setTonePlaying] = useState(false)

  // ── Refs for live audio plumbing (kept out of render) ──
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const segRefs = useRef<Array<HTMLSpanElement | null>>([])
  const recorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunks = useRef<Blob[]>([])
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null)

  // ── Teardown of all live audio ──
  const stopMeter = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    analyserRef.current = null
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined)
      audioCtxRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const paintMeter = useCallback((level: number) => {
    const lit = Math.round(level * METER_SEGMENTS)
    for (let i = 0; i < METER_SEGMENTS; i++) {
      const el = segRefs.current[i]
      if (!el) continue
      let cls = 'audio-check__seg'
      if (i < lit) {
        if (i >= METER_SEGMENTS - 2) cls += ' audio-check__seg--peak'
        else if (i >= METER_SEGMENTS - 6) cls += ' audio-check__seg--hot'
        else cls += ' audio-check__seg--on'
      }
      el.className = cls
    }
    if (level > 0.12) setMicLevelDetected(true)
  }, [])

  // ── Start (or restart) the mic stream + analyser for a given device ──
  const startMic = useCallback(
    async (deviceId?: string) => {
      stopMeter()
      try {
        const constraints: MediaStreamConstraints = {
          audio: deviceId ? {deviceId: {exact: deviceId}} : true,
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        streamRef.current = stream
        setMicPermission('granted')

        const Ctx =
          window.AudioContext ||
          (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext
        const ctx = new Ctx()
        audioCtxRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)
        analyserRef.current = analyser

        const data = new Uint8Array(analyser.fftSize)
        const loop = () => {
          const active = analyserRef.current
          if (!active) return
          active.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128
            sum += v * v
          }
          const level = Math.min(1, Math.sqrt(sum / data.length) * 3.2)
          paintMeter(level)
          rafRef.current = requestAnimationFrame(loop)
        }
        rafRef.current = requestAnimationFrame(loop)
      } catch (err) {
        const name = (err as DOMException)?.name
        setMicPermission(
          name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'prompt'
        )
      }
    },
    [paintMeter, stopMeter]
  )

  // ── Explicitly ask the browser for mic access (triggers the prompt) ──
  const requestPermission = useCallback(() => {
    void startMic(selectedInput || undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMic, selectedInput])

  // ── Enumerate devices (labels appear only after permission) ──
  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      setInputs(devices.filter((d) => d.kind === 'audioinput'))
      setOutputs(devices.filter((d) => d.kind === 'audiooutput'))
    } catch (err) {
      // ignore — keep whatever we have
    }
  }, [])

  // ── On open: query permission, start mic, enumerate ──
  useEffect(() => {
    if (!show) return
    let cancelled = false

    const init = async () => {
      try {
        const status = await (
          navigator.permissions as unknown as {
            query: (d: {name: string}) => Promise<{
              state: string
              onchange: (() => void) | null
            }>
          }
        ).query({name: 'microphone'})
        const apply = () => {
          if (!cancelled) setMicPermission(status.state as 'granted' | 'prompt' | 'denied')
        }
        apply()
        status.onchange = apply
      } catch {
        // permissions API or 'microphone' name unsupported (e.g. Firefox) — resolve via getUserMedia
      }
      await startMic(selectedInput || undefined)
      if (!cancelled) await refreshDevices()
    }
    void init()

    return () => {
      cancelled = true
      stopMeter()
      if (playbackAudioRef.current) {
        playbackAudioRef.current.pause()
        playbackAudioRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // ── React to mic device change ──
  const handleInputChange = useCallback(
    (deviceId: string) => {
      setSelectedInput(deviceId)
      localStorage.setItem(STORAGE_INPUT, deviceId)
      setMicLevelDetected(false)
      setMicConfirmed(null)
      void startMic(deviceId || undefined)
    },
    [startMic]
  )

  const handleOutputChange = useCallback((deviceId: string) => {
    setSelectedOutput(deviceId)
    localStorage.setItem(STORAGE_OUTPUT, deviceId)
    setSpeakerConfirmed(null)
  }, [])

  // ── Speaker test tone (routed to the selected output when supported) ──
  const playTestTone = useCallback(async () => {
    setTonePlaying(true)
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext
      const ctx = new Ctx()
      const dest = ctx.createMediaStreamDestination()
      const now = ctx.currentTime
      ;[
        [660, 0],
        [880, 0.18],
      ].forEach(([freq, at]) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        osc.connect(gain)
        gain.connect(dest)
        gain.connect(ctx.destination)
        gain.gain.setValueAtTime(0, now + at)
        gain.gain.linearRampToValueAtTime(0.18, now + at + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.34)
        osc.start(now + at)
        osc.stop(now + at + 0.36)
      })

      if (selectedOutput && supportsSinkId()) {
        const audio = new Audio()
        audio.srcObject = dest.stream
        await (audio as unknown as {setSinkId: (id: string) => Promise<void>}).setSinkId(
          selectedOutput
        )
        await audio.play().catch(() => undefined)
      }
    } catch {
      // best effort
    }
    setTimeout(() => setTonePlaying(false), 800)
  }, [selectedOutput])

  // ── Record & play back ──
  const recordAndPlayback = useCallback(async () => {
    const stream = streamRef.current
    if (!stream) return
    try {
      recordedChunks.current = []
      const recorder = new MediaRecorder(stream)
      recorderRef.current = recorder
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data)
      }
      recorder.onstop = () => {
        setRecording(false)
        setPlayingBack(true)
        const blob = new Blob(recordedChunks.current, {type: 'audio/webm'})
        const audio = new Audio(URL.createObjectURL(blob))
        playbackAudioRef.current = audio
        if (selectedOutput && supportsSinkId()) {
          void (audio as unknown as {setSinkId: (id: string) => Promise<void>})
            .setSinkId(selectedOutput)
            .catch(() => undefined)
        }
        audio.onended = () => {
          setPlayingBack(false)
          setRecordingDone(true)
        }
        void audio.play().catch(() => {
          setPlayingBack(false)
          setRecordingDone(true)
        })
      }
      setRecording(true)
      setRecordingDone(false)
      recorder.start()
      setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop()
      }, 4000)
    } catch {
      setRecording(false)
    }
  }, [selectedOutput])

  const micGood = micLevelDetected && micConfirmed !== false
  const speakerGood = speakerConfirmed === true

  const goTo = useCallback((next: StepIndex) => setStep(next), [])

  const stepNodes = useMemo(
    () => [
      {label: intl.formatMessage(M.stepMic), done: micGood && step > 0},
      {label: intl.formatMessage(M.stepSpeaker), done: speakerGood && step > 1},
      {label: intl.formatMessage(M.stepReady), done: false},
    ],
    [intl, micGood, speakerGood, step]
  )

  if (!show) return null

  return (
    <div className='audio-check' role='dialog' aria-label={intl.formatMessage(M.dialogLabel)}>
      <header className='audio-check__head'>
        <h2 className='audio-check__title'>
          <span aria-hidden='true'>{GLYPH.headset}</span> {intl.formatMessage(M.title)}
        </h2>
        <p className='audio-check__subtitle'>{intl.formatMessage(M.subtitle)}</p>
        <button
          type='button'
          className='audio-check__x'
          aria-label={intl.formatMessage(M.close)}
          onClick={onClose}
        >
          {GLYPH.ko}
        </button>
      </header>

      <nav className='audio-check__stepper'>
        {stepNodes.map((node, i) => (
          <button
            type='button'
            key={node.label}
            className={`audio-check__node${i === step ? ' is-active' : ''}${
              node.done ? ' is-done' : ''
            }`}
            onClick={() => goTo(i as StepIndex)}
          >
            <span className='audio-check__num'>{node.done ? GLYPH.ok : i + 1}</span>
            <span className='audio-check__cap'>{node.label}</span>
          </button>
        ))}
      </nav>

      <div className='audio-check__body'>
        {/* Step 1 — Microphone */}
        {step === 0 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                {GLYPH.mic}
              </span>
              <div>
                <h3>{intl.formatMessage(M.micTitle)}</h3>
                <p>{intl.formatMessage(M.micHint)}</p>
              </div>
            </div>

            <div className='audio-check__field'>
              <label htmlFor='audio-check-mic'>{intl.formatMessage(M.stepMic)}</label>
              <select
                id='audio-check-mic'
                value={selectedInput}
                onChange={(e) => handleInputChange(e.target.value)}
              >
                <option value=''>{intl.formatMessage(M.micDefault)}</option>
                {inputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label ||
                      intl.formatMessage(M.micFallbackName, {id: d.deviceId.slice(0, 6)})}
                  </option>
                ))}
              </select>
            </div>

            <p className='audio-check__prompt'>{intl.formatMessage(M.micPrompt)}</p>
            <div className='audio-check__meter' aria-label={intl.formatMessage(M.micLevelLabel)}>
              {Array.from({length: METER_SEGMENTS}).map((_, i) => (
                <span
                  key={i}
                  className='audio-check__seg'
                  ref={(el) => {
                    segRefs.current[i] = el
                  }}
                />
              ))}
            </div>

            <div className='audio-check__row'>
              <button
                type='button'
                className={`audio-check__btn${recording || playingBack ? ' is-busy' : ''}`}
                onClick={recordAndPlayback}
                disabled={micPermission === 'denied'}
              >
                <span aria-hidden='true'>{recordingDone ? GLYPH.replay : GLYPH.record}</span>{SPACE}
                {intl.formatMessage(
                  recording
                    ? M.recording
                    : playingBack
                    ? M.playingBack
                    : recordingDone
                    ? M.recordAgain
                    : M.recordAndPlay
                )}
              </button>
              {recordingDone && (
                <span className='audio-check__ask'>
                  {intl.formatMessage(M.heardYourself)}
                  <span className='audio-check__yn'>
                    <button
                      type='button'
                      className={`audio-check__mini is-yes${
                        micConfirmed === true ? ' is-sel' : ''
                      }`}
                      onClick={() => setMicConfirmed(true)}
                    >
                      {intl.formatMessage(M.yes)}
                    </button>
                    <button
                      type='button'
                      className={`audio-check__mini is-no${
                        micConfirmed === false ? ' is-sel' : ''
                      }`}
                      onClick={() => setMicConfirmed(false)}
                    >
                      {intl.formatMessage(M.no)}
                    </button>
                  </span>
                </span>
              )}
            </div>

            {(micPermission === 'prompt' || micPermission === 'unknown') && !micLevelDetected && (
              <div className='audio-check__row'>
                <button type='button' className='audio-check__btn' onClick={requestPermission}>
                  <span aria-hidden='true'>{GLYPH.micPermission}</span> {intl.formatMessage(M.grantAccess)}
                </button>
              </div>
            )}
            {micPermission === 'denied' && (
              <div className='audio-check__guide'>
                <div className='audio-check__guide-head'>
                  <span aria-hidden='true'>{GLYPH.lock}</span> {intl.formatMessage(M.micBlocked)}
                </div>
                <ol className='audio-check__guide-steps'>
                  {permissionUnblockSteps(intl).map((stepText) => (
                    <li key={stepText}>{stepText}</li>
                  ))}
                </ol>
                <button type='button' className='audio-check__btn' onClick={requestPermission}>
                  <span aria-hidden='true'>{GLYPH.replay}</span> {intl.formatMessage(M.tryAgain)}
                </button>
              </div>
            )}
            {micLevelDetected && micConfirmed !== false && (
              <div className='audio-check__result is-ok'>
                <span aria-hidden='true'>{GLYPH.ok}</span> {intl.formatMessage(M.micOk)}
              </div>
            )}
            {micConfirmed === false && (
              <div className='audio-check__result is-bad'>
                <span aria-hidden='true'>{GLYPH.ko}</span> {intl.formatMessage(M.micBad)}
              </div>
            )}
          </section>
        )}

        {/* Step 2 — Speaker */}
        {step === 1 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                {GLYPH.speaker}
              </span>
              <div>
                <h3>{intl.formatMessage(M.speakerTitle)}</h3>
                <p>{intl.formatMessage(M.speakerHint)}</p>
              </div>
            </div>

            <div className='audio-check__field'>
              <label htmlFor='audio-check-spk'>{intl.formatMessage(M.stepSpeaker)}</label>
              <select
                id='audio-check-spk'
                value={selectedOutput}
                onChange={(e) => handleOutputChange(e.target.value)}
                disabled={!supportsSinkId()}
              >
                <option value=''>{intl.formatMessage(M.speakerDefault)}</option>
                {outputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label ||
                      intl.formatMessage(M.speakerFallbackName, {id: d.deviceId.slice(0, 6)})}
                  </option>
                ))}
              </select>
              {!supportsSinkId() && (
                <p className='audio-check__note'>
                  {intl.formatMessage(M.speakerSystemNote)}
                </p>
              )}
            </div>

            <div className='audio-check__row'>
              <button
                type='button'
                className={`audio-check__btn${tonePlaying ? ' is-busy' : ''}`}
                onClick={playTestTone}
              >
                <span aria-hidden='true'>{GLYPH.play}</span> {intl.formatMessage(M.playTestSound)}
              </button>
              <span className='audio-check__ask'>
                {intl.formatMessage(M.didYouHear)}
                <span className='audio-check__yn'>
                  <button
                    type='button'
                    className={`audio-check__mini is-yes${
                      speakerConfirmed === true ? ' is-sel' : ''
                    }`}
                    onClick={() => setSpeakerConfirmed(true)}
                  >
                    {intl.formatMessage(M.yes)}
                  </button>
                  <button
                    type='button'
                    className={`audio-check__mini is-no${
                      speakerConfirmed === false ? ' is-sel' : ''
                    }`}
                    onClick={() => setSpeakerConfirmed(false)}
                  >
                    {intl.formatMessage(M.no)}
                  </button>
                </span>
              </span>
            </div>

            {speakerConfirmed === true && (
              <div className='audio-check__result is-ok'>
                <span aria-hidden='true'>{GLYPH.ok}</span> {intl.formatMessage(M.speakerOk)}
              </div>
            )}
            {speakerConfirmed === false && (
              <div className='audio-check__result is-bad'>
                <span aria-hidden='true'>{GLYPH.ko}</span> {intl.formatMessage(M.speakerBad)}
              </div>
            )}
          </section>
        )}

        {/* Step 3 — Ready */}
        {step === 2 && (
          <section className='audio-check__pane'>
            <div className='audio-check__pane-head'>
              <span className='audio-check__ic' aria-hidden='true'>
                {GLYPH.check}
              </span>
              <div>
                <h3>{intl.formatMessage(M.readyTitle)}</h3>
                <p>{intl.formatMessage(M.readyHint)}</p>
              </div>
            </div>

            <div className='audio-check__checks'>
              <div className={`audio-check__check${micPermission === 'granted' ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{micPermission === 'granted' ? GLYPH.ok : GLYPH.warn}</span>
                <span>
                  <span className='audio-check__lbl'>
                    {intl.formatMessage(M.checkMicAccess)}
                  </span>
                  {DOT_SEP}
                  <span className='audio-check__sub'>
                    {intl.formatMessage(
                      micPermission === 'granted'
                        ? M.granted
                        : micPermission === 'denied'
                        ? M.blockedFromBar
                        : M.notGrantedYet
                    )}
                  </span>
                </span>
                {micPermission !== 'granted' && (
                  <button
                    type='button'
                    className='audio-check__mini is-yes audio-check__grant'
                    onClick={requestPermission}
                  >
                    {intl.formatMessage(M.grant)}
                  </button>
                )}
              </div>
              <div className={`audio-check__check${isRecommendedBrowser() ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{isRecommendedBrowser() ? GLYPH.ok : GLYPH.warn}</span>
                <span>
                  <span className='audio-check__lbl'>{intl.formatMessage(M.checkBrowser)}</span>
                  {DOT_SEP}
                  <span className='audio-check__sub'>{browserLabel(intl)}</span>
                </span>
              </div>
              <div className={`audio-check__check${window.isSecureContext ? '' : ' is-warn'}`}>
                <span className='audio-check__mark'>{window.isSecureContext ? GLYPH.ok : GLYPH.warn}</span>
                <span>
                  <span className='audio-check__lbl'>
                    {intl.formatMessage(M.checkConnection)}
                  </span>
                  {DOT_SEP}
                  <span className='audio-check__sub'>
                    {intl.formatMessage(window.isSecureContext ? M.secureHttps : M.notSecure)}
                  </span>
                </span>
              </div>
            </div>

            <div className={`audio-check__summary${micGood && speakerGood ? '' : ' is-pending'}`}>
              <span className='audio-check__badge'>{micGood && speakerGood ? GLYPH.ok : GLYPH.warn}</span>
              <div>
                <div className='audio-check__st'>
                  {intl.formatMessage(
                    micGood && speakerGood
                      ? M.summaryOk
                      : micGood
                      ? M.summarySpeakerPending
                      : speakerGood
                      ? M.summaryMicPending
                      : M.summaryNonePending
                  )}
                </div>
                <div className='audio-check__ss'>
                  {intl.formatMessage(
                    micGood && speakerGood ? M.summaryBothPassed : M.summaryOptional
                  )}
                </div>
              </div>
            </div>

            <p className='audio-check__tip'>
              <span aria-hidden='true'>{GLYPH.bulb}</span>
              <span>
                <FormattedMessage {...M.tip} values={{b: (c) => <strong>{c}</strong>}} />
              </span>
            </p>
          </section>
        )}
      </div>

      <footer className='audio-check__foot'>
        <button type='button' className='audio-check__skip' onClick={onClose}>
          {intl.formatMessage(step === 2 ? M.close : M.skipForNow)}
        </button>
        <span className='audio-check__spacer' />
        {step > 0 && (
          <button
            type='button'
            className='audio-check__nav is-ghost'
            onClick={() => goTo((step - 1) as StepIndex)}
          >
            {intl.formatMessage(M.back)}
          </button>
        )}
        <button
          type='button'
          className='audio-check__nav is-primary'
          onClick={() => (step < 2 ? goTo((step + 1) as StepIndex) : onClose())}
        >
          {intl.formatMessage(step === 2 ? M.continueToPolicyDial : M.next)}
        </button>
      </footer>
    </div>
  )
}

export default AudioDeviceCheck
